import { MapNumberTo, MapStringTo, Nullable, Object_Unknown } from "../../..";
import { IContentHubClient } from "../../../clients/content-hub-client";
import DirtyValueCalculator from "../../../dirty-value-calculator";
import Guard from "../../../guard";
import { EntityLoadConfiguration } from "../../querying/entity-load-configuration";
import { ChildRelationBase, IChildRelation } from "../relation";
import { RelationRole } from "../relation-role";
import { IToManyRelation } from "./to-many-relation";

/**
 * A relation where the child has many parents.
 */
export interface IChildToManyParentsRelation extends IChildRelation, IToManyRelation {
    /**
     * The list of parent ids.
     */
    readonly parents: Array<number>;

    /**
     * When related items are partially loaded, this value will be set
     *  to indicate how many related items there are in total.
     */
    parentTotal?: number;

    /**
     * Gets the properties for a parent.
     *
     * If no properties are found, an empty list is returned.
     * @param id - The id of the parent to get properties for
     */
    getParentProperties(id: number): MapStringTo<Object_Unknown>;
}

export class ChildToManyParentsRelation extends ChildRelationBase implements IChildToManyParentsRelation {
    private readonly _parents: Array<number> = [];

    readonly role: RelationRole = RelationRole.Child;

    readonly parentTotal?: number;

    readonly isMultiValue: boolean = true;

    get isFullyLoaded(): boolean {
        return this.parentTotal == null || this._parents.length === this.parentTotal;
    }

    get isDirty(): boolean {
        if (!this.isTracking) {
            return false;
        } else {
            return this._dirtyValueCalculator.isDirty(this._parents);
        }
    }

    get parents(): Array<number> {
        return this._parents.slice();
    }

    constructor(
        name: string,
        properties: Nullable<MapNumberTo<MapStringTo<Object_Unknown>>> = null,
        client?: IContentHubClient,
        parentTotal?: number,
        inheritsSecurity?: boolean
    ) {
        super(name, properties, client, inheritsSecurity);
        this.parentTotal = parentTotal;
    }

    getIds(): Array<number> {
        return this._parents.slice();
    }

    setIds(ids: Array<number>): void {
        Guard.validIds(ids);

        this._parents.splice(0, this._parents.length);

        ids.forEach(id => {
            this._parents.push(id);
        });
    }

    clear(): void {
        this._parents.splice(0, this._parents.length);
    }

    add(id: number): void {
        Guard.validId(id);

        if (!this._parents.includes(id)) {
            this._parents.push(id);
        }
    }

    addRange(ids: Array<number>): void {
        Guard.validIds(ids);

        ids.forEach(id => {
            if (!this._parents.includes(id)) {
                this._parents.push(id);
            }
        });
    }

    async addAsync(identifier: string): Promise<void> {
        Guard.notNullOrUndefined(this.client);
        Guard.stringNotNullOrEmpty(identifier);

        const entity = await this.client!.entities.getAsync(identifier, EntityLoadConfiguration.Minimal);

        if (entity && entity.id) {
            this.add(entity.id);
        }
    }

    async addRangeAsync(identifiers: Array<string>): Promise<void> {
        Guard.notNullOrUndefined(this.client);
        Guard.arrayNoneNullOrEmptyString(identifiers);

        const entities = await this.client!.entities.getManyAsync(identifiers, EntityLoadConfiguration.Minimal);

        if (entities) {
            this.addRange(entities.filter(x => x.id !== null).map(x => x.id!));
        }
    }

    startTracking(): void {
        if (this.isTracking) return;

        this.isTracking = true;

        this._dirtyValueCalculator = new DirtyValueCalculator();
        this._dirtyValueCalculator.setOriginalValue(this._parents);
    }

    markClean(): void {
        if (this.isTracking) {
            this._dirtyValueCalculator.setOriginalValue(this._parents);
        }
    }

    getParentProperties(id: number): MapStringTo<Object_Unknown> {
        return this.properties[id] == null ? {} : this.properties[id];
    }
}
